import { useState, useEffect, useRef } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { BtnSpinner } from "@/components/ui/btn-spinner";
import BtnArrow from "@/components/ui/btn-arrow";
import { toast } from "sonner";
import FileUploader, {
  UploadedFile,
  activeUploadProgress,
  uploadRefs,
} from "@/components/forms/FileUploader";
import { snapshotJourney } from "@/lib/journey";


type LabsFieldKey =
  | "sessionMethod"
  | "experience"
  | "projectStatus"
  | "projectName"
  | "name"
  | "company"
  | "email";

const INPUT_CLASS =
  "w-full bg-transparent border-b border-labs-fg/15 px-0 py-3 text-sm text-labs-fg placeholder:text-labs-fg/30 focus:outline-none focus:border-labs-fg/50 transition-all duration-300";

const SELECT_TRIGGER_CLASS =
  "w-full bg-transparent border-0 border-b border-labs-fg/15 rounded-none px-0 py-3 text-sm text-labs-fg/50 focus:ring-0 focus:border-labs-fg/50 transition-colors";

const SELECT_ITEM_CLASS =
  "font-light text-labs-fg/85 focus:bg-labs-fg/10 focus:text-labs-fg data-[highlighted]:bg-labs-fg/10 data-[highlighted]:text-labs-fg data-[state=checked]:text-labs-fg";

const EYEBROW_CLASS =
  "text-labs-fg/50 font-light text-[10px] tracking-[0.25em] uppercase mb-6 pt-2";

import { LABS_EXPERIENCE_OPTIONS as EXPERIENCE_OPTIONS } from "@/data/projectTypes";

const PROJECT_STATUS_OPTIONS = [
  {
    value: "We are planning an upcoming project",
    desc: "Pre-design phase. The presentation is tailored to your project's sector and helps identify the right codelumen engagement path.",
  },
  {
    value: "We have a project currently in design",
    desc: "Active project in design. The presentation is shaped around your sector so the team can evaluate codelumen's capabilities in context.",
  },
  {
    value: "We have a lighting package designed by others",
    desc: "The right next step may be a Value Analysis of the existing design.",
  },
  {
    value: "We are an active codelumen client",
    desc: "Project-specific testing can be arranged as part of your Architectural Lighting Design engagement.",
  },
];

interface Props {
  /** Optional callback when submission succeeds (after the typing animation closes). */
  onSuccess?: () => void;
}

/**
 * Reusable Labs booking form. Pure form + confirmation dialog.
 * Section chrome (heading, eyebrow, animated reveal) lives in the parent.
 */
const LabsBookingForm = ({ onSuccess }: Props) => {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("");
  const [groupSize, setGroupSize] = useState("");
  const [experience, setExperience] = useState("");
  const [projectStatus, setProjectStatus] = useState("");
  const [spaces, setSpaces] = useState("");
  const [projectName, setProjectName] = useState("");
  const [currentPhase, setCurrentPhase] = useState("");
  const [sessionMethod, setSessionMethod] = useState("");
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const uploadPct = activeUploadProgress(files);
  const uploadsPending = files.some((f) => f.status === "uploading" || f.status === "queued");
  const uploadsFailed = files.some((f) => f.status === "failed");
  const [submitted, setSubmitted] = useState(false);
  const [submittedAt, setSubmittedAt] = useState<Date | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [displayedText, setDisplayedText] = useState("");
  const [typingDone, setTypingDone] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<LabsFieldKey, string>>>({});
  const [hp, setHp] = useState("");
  const startedAtRef = useRef<number>(Date.now());

  const fieldRefs = useRef<Record<LabsFieldKey, HTMLElement | null>>({
    sessionMethod: null,
    experience: null,
    projectStatus: null,
    projectName: null,
    name: null,
    company: null,
    email: null,
  });
  const FIELD_ORDER: LabsFieldKey[] = [
    "sessionMethod",
    "experience",
    "projectStatus",
    "projectName",
    "name",
    "company",
    "email",
  ];

  const clearFieldError = (key: LabsFieldKey) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const errorRing = (key: LabsFieldKey) =>
    fieldErrors[key]
      ? "border-destructive/80 focus:border-destructive placeholder:text-destructive/60"
      : "";

  const hasLiveProject = projectStatus === "We are an active codelumen client";

  const confirmationLines = [
    "Your project is our priority.",
    "A codelumen strategist will contact you within one business day.",
  ];
  const fullText = confirmationLines.join("\n");

  useEffect(() => {
    const applyPreselect = () => {
      try {
        const f = sessionStorage.getItem("labs:visitFocus");
        const v = sessionStorage.getItem("labs:visitType");
        const fmt = sessionStorage.getItem("labs:sessionMethod");
        if (f) {
          const match = EXPERIENCE_OPTIONS.find((o) =>
            o.value.toLowerCase().includes(f.toLowerCase()),
          );
          if (match) setExperience(match.value);
          sessionStorage.removeItem("labs:visitFocus");
        }
        if (fmt === "In Person" || fmt === "Virtual") {
          setSessionMethod(fmt);
          sessionStorage.removeItem("labs:sessionMethod");
        }
        if (v) sessionStorage.removeItem("labs:visitType");
      } catch {
        /* ignore */
      }
    };
    applyPreselect();
    window.addEventListener("labs:preselect", applyPreselect);
    return () => window.removeEventListener("labs:preselect", applyPreselect);
  }, []);

  // Defense in depth: any time a field becomes valid, drop its stale error
  // immediately. Prevents "Choose the project category…" from lingering after
  // the user has actually picked one (observed on mobile).
  useEffect(() => {
    setFieldErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      if (next.sessionMethod && sessionMethod) delete next.sessionMethod;
      if (next.experience && experience) delete next.experience;
      if (next.projectStatus && projectStatus) delete next.projectStatus;
      if (next.projectName && projectName.trim()) delete next.projectName;
      if (next.name && name.trim()) delete next.name;
      if (next.company && company.trim()) delete next.company;
      if (
        next.email &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
      ) {
        delete next.email;
      }
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [sessionMethod, experience, projectStatus, projectName, name, company, email]);

  // Clear the top-level banner error the moment any field is corrected.
  useEffect(() => {
    if (submitError && Object.keys(fieldErrors).length === 0) {
      setSubmitError(null);
    }
  }, [fieldErrors, submitError]);

  useEffect(() => {
    if (!submitted) return;
    let i = 0;
    const interval = setInterval(() => {
      if (i < fullText.length) {
        setDisplayedText(fullText.slice(0, i + 1));
        i++;
      } else {
        clearInterval(interval);
        setTypingDone(true);
      }
    }, 35);
    return () => clearInterval(interval);
  }, [submitted, fullText]);

  // Note: the post-submit status view stays open until the user dismisses it
  // (or the parent unmounts), so they have time to read what happens next.

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    // Client-side validation: mirror the required-field model. We collect every
    // missing/invalid field so the UI can highlight all of them at once, and
    // we move focus to the first one in form order.
    const errs: Partial<Record<LabsFieldKey, string>> = {};
    if (!sessionMethod) errs.sessionMethod = "Choose an in-person or virtual format.";
    if (!experience) errs.experience = "Choose the project category we'll discuss.";
    if (!projectStatus) errs.projectStatus = "Tell us your project status.";
    if (hasLiveProject && !projectName.trim()) {
      errs.projectName = "Add the project name so we can prepare.";
    }
    if (!name.trim()) errs.name = "Your name is required.";
    if (!company.trim()) errs.company = "Company / studio is required.";
    if (!email.trim()) {
      errs.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errs.email = "Enter a valid email address.";
    }

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      const firstKey = FIELD_ORDER.find((k) => errs[k]);
      const firstMsg = firstKey ? errs[firstKey] : "Some fields need attention.";
      setSubmitError(firstMsg ?? null);
      toast.error("Please check your details", {
        description: firstMsg ?? "Some fields need attention.",
      });
      requestAnimationFrame(() => {
        if (!firstKey) return;
        const el = fieldRefs.current[firstKey];
        if (el && typeof (el as HTMLElement).focus === "function") {
          (el as HTMLElement).focus();
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
      return;
    }

    setFieldErrors({});
    if (uploadsPending) {
      toast.error("Upload still in progress", { description: "Please wait for uploads to finish." });
      return;
    }
    if (uploadsFailed) {
      toast.error("Some uploads failed", { description: "Please retry or remove failed files." });
      return;
    }

    setSubmitting(true);
    try {
      const uploads = uploadRefs(files);
      const spacesParts = [
        experience ? `Experience: ${experience}` : null,
        projectStatus ? `Project status: ${projectStatus}` : null,
        uploads.length > 0
          ? `Attachments: ${uploads.map((u) => u.filename).join(", ")}`
          : null,
        spaces ? `Notes: ${spaces}` : null,
      ].filter(Boolean);

      // The edge function only accepts a fixed set of `visitType` values.
      const visitType = hasLiveProject
        ? "Project Strategy Session"
        : "Lab Walkthrough";

      const journey = snapshotJourney();

      const { data, error } = await supabase.functions.invoke('send-tour-request', {
        body: {
          name,
          company,
          email,
          phone,
          role,
          groupSize,
          visitType,
          spaces: spacesParts.join(" · "),
          projectName,
          currentPhase,
          sessionMethod,
          visitFocus: experience,
          uploads,
          hp,
          started_ms: Date.now() - startedAtRef.current,
          journey,
        },
      });


      // Labs bookings: the customer confirmation is sent by send-tour-request.
      // We still push the lead into Zoho CRM via send-form-submission, which
      // suppresses its own confirmation for source_form: "labs-booking" to
      // avoid a duplicate/wrong email.
      supabase.functions
        .invoke("send-form-submission", {
          body: {
            full_name: name,
            email,
            company,
            phone: phone || undefined,
            message: spaces || undefined,
            source_form: "labs-booking",
            details: {
              role: role || undefined,
              experience: experience || undefined,
              project_status: projectStatus || undefined,
              session_method: sessionMethod || undefined,
              group_size: groupSize || undefined,
              project_name: projectName || undefined,
              project_phase: currentPhase || undefined,
            },
            uploads,
            hp,
            started_ms: Date.now() - startedAtRef.current,
            journey,
          },
        })
        .catch((err) => console.warn("CRM sync failed:", err));



      if (error) {
        const msg = "Something went wrong. Please try again.";
        setSubmitError(msg);
        toast.error("Submission failed", { description: msg });
        setSubmitting(false);
        return;
      }

      if (data?.error) {
        const msg = "Something went wrong. Please try again.";
        setSubmitError(msg);
        toast.error("Submission failed", { description: msg });
        setSubmitting(false);
        return;
      }

      setSubmittedAt(new Date());
      setSubmittedEmail(email);
      setSubmitted(true);
      toast.success("Request received", {
        description: "A senior team member will respond within one business day.",
      });
    } catch {
      const msg = "Network error. Please check your connection and try again.";
      setSubmitError(msg);
      toast.error("Network error", { description: msg });
    }

    setSubmitting(false);
  };

  const handleDialogClose = () => {
    setSubmitted(false);
    setDisplayedText("");
    setTypingDone(false);
    setName(""); setCompany(""); setEmail(""); setPhone("");
    setRole(""); setGroupSize(""); setExperience(""); setProjectStatus("");
    setSpaces(""); setProjectName(""); setCurrentPhase(""); setSessionMethod("");
    setFiles([]);
    onSuccess?.();
  };

  const sectionDivider = "border-t border-labs-fg/5 pt-12";
  // Section numbering shifts when the live-project context block is hidden.
  const aboutYouNumber = hasLiveProject ? "05" : "04";
  const assetsNumber = hasLiveProject ? "06" : "05";

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-14 form-mobile-pad" noValidate>
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={hp}
          onChange={(e) => setHp(e.target.value)}
          aria-hidden="true"
          className="sr-only"
        />
        {/* Trust line */}
        <p className="text-labs-fg/40 font-light text-[10px] tracking-[0.25em] uppercase text-center">
          A private session. Real human reply. No newsletter.
        </p>

        {/* 01 · How would you like to experience codelumenLabs? */}
        <div>
          <p className={EYEBROW_CLASS}>01 · How would you like to experience codelumenLabs?</p>
          <p className="text-labs-fg/70 font-light text-sm mb-5">
            {sessionMethod === "In Person"
              ? "Experience beam control, optics, controls, scenes, finishes, and lighting decisions at full scale in our private El Paso environment."
              : sessionMethod === "Virtual"
              ? "Join a live, guided presentation with a senior codelumen team member, tailored to your project category and delivered remotely."
              : "Both formats are tailored to your project. Visit codelumenLabs for the full physical, immersive experience, or schedule a live virtual presentation from anywhere in the world."}
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              {
                value: "In Person",
                title: "Visit codelumenLabs · El Paso",
                desc: "The full physical, immersive environment. Beam angles, scenes, controls, and finishes at full scale, tested against your project.",
              },
              {
                value: "Virtual",
                title: "Virtual codelumenLabs Presentation",
                desc: "A live, guided presentation with a senior codelumen team member, tailored to your project and delivered remotely.",
              },
            ].map((opt, idx) => {
              const active = sessionMethod === opt.value;
              return (
                <label
                  key={opt.value}
                  className={`relative block cursor-pointer border p-5 min-h-[132px] transition-all duration-300 ${
                    active
                      ? "border-labs-fg bg-labs-fg/[0.04]"
                      : "border-labs-fg/15 hover:border-labs-fg/45"
                  } ${fieldErrors.sessionMethod ? "border-destructive/70" : ""}`}
                >
                  <input
                    type="radio"
                    name="sessionMethod"
                    value={opt.value}
                    required
                    ref={(el) => {
                      if (idx === 0) fieldRefs.current.sessionMethod = el;
                    }}
                    checked={active}
                    onChange={(e) => {
                      setSessionMethod(e.target.value);
                      clearFieldError("sessionMethod");
                    }}
                    aria-invalid={!!fieldErrors.sessionMethod}
                    className="sr-only"
                  />
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-labs-fg text-sm">{opt.title}</span>
                    <span
                      aria-hidden
                      className={`mt-1 shrink-0 w-1.5 h-1.5 rounded-full transition-colors duration-300 ${
                        active ? "bg-labs-fg" : "bg-labs-fg/25"
                      }`}
                    />
                  </div>
                  <p className="text-labs-fg/55 text-xs mt-2 font-light leading-relaxed">
                    {opt.desc}
                  </p>
                </label>
              );
            })}
          </div>
          {fieldErrors.sessionMethod && (
            <p className="mt-3 text-destructive text-[11px] font-light">{fieldErrors.sessionMethod}</p>
          )}
        </div>

        {/* 02 · What type of project will we be discussing? */}
        <div className={sectionDivider}>
          <p className={EYEBROW_CLASS}>02 · What type of project will we be discussing?</p>
          <p className="text-labs-fg/70 font-light text-sm mb-5">
            The session is shaped around your project category, so the team can prepare the most relevant demonstrations and references.
          </p>
          <div className="space-y-4">
            {EXPERIENCE_OPTIONS.map((opt, idx) => (
              <label key={opt.value} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="experience"
                  value={opt.value}
                  required
                  ref={(el) => {
                    if (idx === 0) fieldRefs.current.experience = el;
                  }}
                  checked={experience === opt.value}
                  onChange={(e) => {
                    setExperience(e.target.value);
                    clearFieldError("experience");
                  }}
                  aria-invalid={!!fieldErrors.experience}
                  className={`mt-0.5 accent-labs-fg ${
                    fieldErrors.experience ? "ring-2 ring-destructive/60 rounded-full" : ""
                  }`}
                />
                <div>
                  <span className="text-labs-fg text-sm">{opt.value}</span>
                  <p className="text-labs-fg/55 text-xs mt-0.5 font-light">{opt.desc}</p>
                </div>
              </label>
            ))}
          </div>
          {fieldErrors.experience && (
            <p className="mt-3 text-destructive text-[11px] font-light">{fieldErrors.experience}</p>
          )}
        </div>

        {/* 03 · Live project status */}
        <div className={sectionDivider}>
          <p className={EYEBROW_CLASS}>03 · Where you are with a project</p>
          <p className="text-labs-fg/70 font-light text-sm mb-5">
            codelumenLabs is reserved for project teams. Tell us where you are so we can tailor the presentation and recommend the right next path.
          </p>
          <div className="space-y-4">
            {PROJECT_STATUS_OPTIONS.map((opt, idx) => (
              <label key={opt.value} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="projectStatus"
                  value={opt.value}
                  required
                  ref={(el) => {
                    if (idx === 0) fieldRefs.current.projectStatus = el;
                  }}
                  checked={projectStatus === opt.value}
                  onChange={(e) => {
                    setProjectStatus(e.target.value);
                    clearFieldError("projectStatus");
                  }}
                  aria-invalid={!!fieldErrors.projectStatus}
                  className={`mt-0.5 accent-labs-fg ${
                    fieldErrors.projectStatus ? "ring-2 ring-destructive/60 rounded-full" : ""
                  }`}
                />
                <div>
                  <span className="text-labs-fg text-sm">{opt.value}</span>
                  <p className="text-labs-fg/55 text-xs mt-0.5 font-light">{opt.desc}</p>
                </div>
              </label>
            ))}
          </div>
          {fieldErrors.projectStatus && (
            <p className="mt-3 text-destructive text-[11px] font-light">{fieldErrors.projectStatus}</p>
          )}
        </div>

        {/* 04 · Project Context (only when a live project exists) */}
        {hasLiveProject && (
          <div className={sectionDivider}>
            <p className={EYEBROW_CLASS}>04 · Project Context</p>
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                <div>
                  <input
                    type="text"
                    placeholder="Project name"
                    required
                    ref={(el) => { fieldRefs.current.projectName = el; }}
                    value={projectName}
                    onChange={(e) => {
                      setProjectName(e.target.value);
                      clearFieldError("projectName");
                    }}
                    maxLength={500}
                    aria-invalid={!!fieldErrors.projectName}
                    className={`${INPUT_CLASS} ${errorRing("projectName")}`}
                    autoComplete="off"
                  />
                  {fieldErrors.projectName && (
                    <p className="mt-2 text-destructive text-[11px] font-light">{fieldErrors.projectName}</p>
                  )}
                </div>
                <Select value={currentPhase} onValueChange={setCurrentPhase}>
                  <SelectTrigger aria-label="Where is the project today?" className={SELECT_TRIGGER_CLASS}>
                    <SelectValue placeholder="Where is the project today?" />
                  </SelectTrigger>
                  <SelectContent className="bg-labs-bg border border-labs-fg/10 z-50">
                    <SelectItem value="Early planning / pre-design" className={SELECT_ITEM_CLASS}>Early planning / pre-design</SelectItem>
                    <SelectItem value="Design in progress" className={SELECT_ITEM_CLASS}>Design in progress</SelectItem>
                    <SelectItem value="Drawings issued, pre-construction" className={SELECT_ITEM_CLASS}>Drawings issued, pre-construction</SelectItem>
                    <SelectItem value="Out for bid / value engineering" className={SELECT_ITEM_CLASS}>Out for bid / value engineering</SelectItem>
                    <SelectItem value="Under construction" className={SELECT_ITEM_CLASS}>Under construction</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <input type="text" placeholder="Anything else about the space we should know? (optional)" value={spaces} onChange={(e) => setSpaces(e.target.value)} maxLength={500} className={INPUT_CLASS} />
                <p className="text-labs-fg/50 text-[11px] font-light mt-2">Helps us prepare relevant demonstrations.</p>
              </div>
            </div>
          </div>
        )}

        {/* About You */}
        <div className={sectionDivider}>
          <p className={EYEBROW_CLASS}>{aboutYouNumber} · About You</p>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
            <div>
              <input
                type="text"
                placeholder="Name"
                required
                ref={(el) => { fieldRefs.current.name = el; }}
                value={name}
                onChange={(e) => { setName(e.target.value); clearFieldError("name"); }}
                maxLength={500}
                aria-invalid={!!fieldErrors.name}
                className={`${INPUT_CLASS} ${errorRing("name")}`}
                autoComplete="name"
              />
              {fieldErrors.name && (
                <p className="mt-2 text-destructive text-[11px] font-light">{fieldErrors.name}</p>
              )}
            </div>
            <div>
              <input
                type="text"
                placeholder="Company / Studio"
                required
                ref={(el) => { fieldRefs.current.company = el; }}
                value={company}
                onChange={(e) => { setCompany(e.target.value); clearFieldError("company"); }}
                maxLength={500}
                aria-invalid={!!fieldErrors.company}
                className={`${INPUT_CLASS} ${errorRing("company")}`}
                autoComplete="organization"
              />
              {fieldErrors.company && (
                <p className="mt-2 text-destructive text-[11px] font-light">{fieldErrors.company}</p>
              )}
            </div>
            <div>
              <input
                type="email"
                placeholder="Email"
                required
                ref={(el) => { fieldRefs.current.email = el; }}
                value={email}
                onChange={(e) => { setEmail(e.target.value); clearFieldError("email"); }}
                maxLength={255}
                aria-invalid={!!fieldErrors.email}
                className={`${INPUT_CLASS} ${errorRing("email")}`}
                autoComplete="email"
              />
              {fieldErrors.email && (
                <p className="mt-2 text-destructive text-[11px] font-light">{fieldErrors.email}</p>
              )}
            </div>
            <input type="tel" placeholder="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={500} className={INPUT_CLASS} autoComplete="tel" />
            <div>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger aria-label="Your role" className={SELECT_TRIGGER_CLASS}>
                  <SelectValue placeholder="Your role" />
                </SelectTrigger>
                <SelectContent className="bg-labs-bg border border-labs-fg/10 z-50">
                  {["Architect", "Developer", "Contractor", "Engineer", "Owner", "Other"].map(r => (
                    <SelectItem key={r} value={r} className={SELECT_ITEM_CLASS}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-labs-fg/50 text-[11px] font-light mt-2">Helps us tailor your session.</p>
            </div>
            <div>
              <Select value={groupSize} onValueChange={setGroupSize}>
                <SelectTrigger aria-label="Group size" className={SELECT_TRIGGER_CLASS}>
                  <SelectValue placeholder="Group size" />
                </SelectTrigger>
                <SelectContent className="bg-labs-bg border border-labs-fg/10 z-50">
                  <SelectItem value="1-2" className={SELECT_ITEM_CLASS}>1–2</SelectItem>
                  <SelectItem value="3-5" className={SELECT_ITEM_CLASS}>3–5</SelectItem>
                  <SelectItem value="6+" className={SELECT_ITEM_CLASS}>6+</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-labs-fg/50 text-[11px] font-light mt-2">Private sessions are intentionally small.</p>
            </div>
          </div>
        </div>

        {/* Project Assets (optional) — always visible */}
        <div className={sectionDivider}>
          <p className={EYEBROW_CLASS}>{assetsNumber} · Project Assets (optional)</p>
          <p className="mb-4 text-labs-fg/60 font-light text-sm">
            Floor plans, drawings, fixture schedules, or renderings. Up to 10 files, 300&nbsp;MB each. Maximum total upload size: 3&nbsp;GB.
          </p>
          <FileUploader
            source="labs"
            files={files}
            onChange={setFiles}
            variant="dark"
            helperText="Upload floor plans, drawings, or renderings"
          />
        </div>

        {submitError && <p className="text-destructive text-sm font-light">{submitError}</p>}

        <div className="form-action-bar form-action-bar--light flex-col items-stretch gap-3 pt-4">
          <button
            type="submit"
            disabled={submitting || uploadPct !== null}
            aria-busy={submitting}
            data-loading={submitting || undefined}
            className="group btn-primary-light w-full justify-center min-h-[48px]"
          >
            <span className="btn-label">
              {uploadPct !== null
                ? `Uploading… ${uploadPct}%`
                : submitting
                  ? "Scheduling…"
                  : (
                    <>
                      Schedule Visit{" "}
                      <BtnArrow />
                    </>
                  )}
            </span>
            <BtnSpinner label="Submitting Labs request" />
          </button>
          <p className="text-labs-fg/50 font-light text-[11px] leading-relaxed text-center">
            A senior team member will respond within one business day. Your information is never shared.
          </p>
        </div>

      </form>

      <Dialog open={submitted} onOpenChange={(open) => { if (!open) handleDialogClose(); }}>
        <DialogContent
          className="bg-labs-bg border border-labs-fg/10 max-w-lg mx-auto"
          onInteractOutside={(e) => { if (!typingDone) e.preventDefault(); }}
        >
          <div className="py-4 px-1">
            <p className="text-sm font-light leading-relaxed whitespace-pre-line">
              {displayedText.split("").map((char, i) => {
                const progress = i / fullText.length;
                const h = 30 + progress * 15;
                const s = 100 - progress * 85;
                const l = 55 + progress * 40;
                const glow = `0 0 ${8 + progress * 4}px hsla(${h}, ${s}%, ${l}%, 0.6)`;
                return (
                  <span key={i} style={{ color: `hsl(${h}, ${s}%, ${l}%)`, textShadow: glow, transition: 'color 0.3s, text-shadow 0.3s' }}>{char}</span>
                );
              })}
              {!typingDone && <span className="inline-block w-[2px] h-4 bg-labs-fg/50 animate-pulse ml-0.5 align-middle" />}
            </p>

            {typingDone && submittedAt && (() => {
              const ts = submittedAt;
              const stamp = ts.toLocaleString(undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                timeZoneName: "short",
              });
              const ref = `LB-${ts.getFullYear()}${String(ts.getMonth() + 1).padStart(2, "0")}${String(
                ts.getDate(),
              ).padStart(2, "0")}-${String(ts.getHours()).padStart(2, "0")}${String(ts.getMinutes()).padStart(2, "0")}`;
              return (
                <div className="mt-8 animate-in fade-in duration-700">
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-t border-labs-fg/10 pt-5">
                    <div>
                      <dt className="text-labs-fg/40 font-light text-[10px] tracking-[0.25em] uppercase">Submitted</dt>
                      <dd className="mt-1 text-labs-fg/85 font-light text-xs">{stamp}</dd>
                    </div>
                    <div>
                      <dt className="text-labs-fg/40 font-light text-[10px] tracking-[0.25em] uppercase">Reference</dt>
                      <dd className="mt-1 text-labs-fg/85 font-light text-xs font-mono">{ref}</dd>
                    </div>
                    {submittedEmail && (
                      <div className="col-span-2">
                        <dt className="text-labs-fg/40 font-light text-[10px] tracking-[0.25em] uppercase">Confirmation to</dt>
                        <dd className="mt-1 text-labs-fg/85 font-light text-xs break-all">{submittedEmail}</dd>
                      </div>
                    )}
                  </dl>

                  <p className="mt-6 text-labs-fg/40 font-light text-[10px] tracking-[0.25em] uppercase">
                    What happens next
                  </p>
                  <ol className="mt-4 space-y-4">
                    {[
                      { t: "Within one business day", d: "A senior strategist replies to confirm fit and propose times." },
                      { t: "Pre-session brief", d: "We share a short prep note tailored to your project category and chosen format." },
                      { t: "Your Labs session", d: sessionMethod === "Virtual" ? "A live, guided presentation with a senior codelumen team member, tailored to your project." : "A private, in-person session in El Paso, tested against your project, scope, and constraints." },
                    ].map((step, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="text-labs-fg/40 font-light text-[10px] tracking-[0.15em] pt-0.5 w-5 shrink-0">
                          0{i + 1}
                        </span>
                        <div>
                          <p className="text-labs-fg font-light text-xs">{step.t}</p>
                          <p className="mt-0.5 text-labs-fg/55 font-light text-xs leading-relaxed">{step.d}</p>
                        </div>
                      </li>
                    ))}
                  </ol>

                  <button
                    type="button"
                    onClick={handleDialogClose}
                    className="mt-8 w-full text-center border border-labs-fg/20 hover:border-labs-fg/50 transition-colors py-3 text-labs-fg/80 hover:text-labs-fg font-light text-[11px] tracking-[0.2em] uppercase"
                  >
                    Close
                  </button>
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LabsBookingForm;
